import type { HostMetrics } from '@amzn/work-spaces-agentic-web-viewer';
import { Dimension } from '@amzn/work-spaces-web-client-core-lib';
import type {
  DimensionSet,
  ILogger,
  IMetrics,
  MetricName,
  Operation,
} from '@amzn/work-spaces-web-client-core-lib';
import { errorNameOf } from './errorNameOf';

// The publisher fills these only when a point lacks them, and keys each report
// by them, so a viewer property of the same name would re-key or drop the point.
const PUBLISHER_DIMENSIONS: ReadonlySet<string> = new Set([
  Dimension.AppName,
  Dimension.Operation,
  Dimension.Version,
  Dimension.LoginType,
]);

// A new object, because the publisher writes its own dimensions into it.
const dimensionsOf = (
  properties: Readonly<Record<string, string>> = {}
): Record<string, string> =>
  Object.fromEntries(
    Object.entries(properties).filter(([key]) => !PUBLISHER_DIMENSIONS.has(key))
  );

// Only emitWithValue: emit and emitWithDimensions write a result as Fault and
// Error points, and the shared web Fault alarm reads every operation's Fault.
export const createAgenticHostMetrics = (
  metrics: IMetrics,
  logger: ILogger
): HostMetrics => {
  const warnNotRecorded = (error: unknown) =>
    logger.warn(`Agentic metric not recorded: error=${errorNameOf(error)}`);
  const warnNotUploaded = (error: unknown) =>
    logger.warn(`Agentic metrics not uploaded: error=${errorNameOf(error)}`);

  return {
    emit: (
      operation: string,
      metric: string,
      value: number,
      properties?: Readonly<Record<string, string>>
    ) => {
      try {
        // The enums lack the agentic names, and the publisher accepts any string.
        metrics.emitWithValue(
          operation as Operation,
          metric as MetricName,
          value,
          dimensionsOf(properties) as DimensionSet
        );
      } catch (error) {
        warnNotRecorded(error);
      }
    },
    flush: () => {
      try {
        metrics.flush().catch(warnNotUploaded);
      } catch (error) {
        warnNotUploaded(error);
      }
    },
  };
};
